import { Alert, Box, Button, Divider, FormControl, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from "@mui/material"
import { useEffect, useReducer, useState } from "react"
import SaveIcon from '@mui/icons-material/Save'

import FieldList          from "../../../../components/UserParameters/FieldList"
import UserParametersContext from "../../../../components/UserParameters/context"
import useInternalFetch   from "../../../../api/apiInternal"
import Loading            from "../../../../components/Loading"
import GeneralError       from "../../../../components/GeneralError"
import { convertDbNumberToHtml } from "../../../../common/Helper"
import config             from "../../../../common/Config"

const INDICATORS = [
	{ value: '',                   label: 'Nenhum'                    },
	{ value: '@pib_cagr_5y',       label: 'CAGR PIB 5 anos'          },
	{ value: '@pib_cagr_10y',      label: 'CAGR PIB 10 anos'         },
	{ value: '@inflation_cagr_5y', label: 'CAGR Inflação 5 anos'     },
	{ value: '@inflation_cagr_10y',label: 'CAGR Inflação 10 anos'    },
]

const LEVELS = [
	{ key: 'low', label: 'Lento'  },
	{ key: 'med', label: 'Médio'  },
	{ key: 'hig', label: 'Rápido' },
]

const GrowthPanel = ({ fields, contextId, onSaved }) => {
	const internalFetch             = useInternalFetch()
	const [, forceUpdate]           = useReducer(x => x + 1, 0)
	const [field, setField]         = useState(null)
	const [values, setValues]       = useState({ low: '', med: '', hig: '' })
	const [indicators, setIndicators] = useState({ low: '', med: '', hig: '' })
	const [errors, setErrors]       = useState({ low: false, med: false, hig: false })
	const [isLoading, setIsLoading] = useState(true)
	const [isSaving, setIsSaving]   = useState(false)
	const [fatalError, setFatalError] = useState(false)
	const [message, setMessage]     = useState(null)

	useEffect(() => {
		setIsLoading(true)
		setFatalError(false)
		const ctxUrl    = config.apiUrl + '/api/settings/growthParameters' +
			(contextId !== null ? `?contextId=${contextId}` : '')
		const globalUrl = config.apiUrl + '/api/settings/growthParameters'

		Promise.all([
			internalFetch(ctxUrl),
			contextId !== null ? internalFetch(globalUrl) : Promise.resolve(null),
		]).then(([ctxResult, globalResult]) => {
			// Use context params if they exist, otherwise fall back to global
			const result = (ctxResult && !ctxResult.status) ? ctxResult
				: (globalResult && !globalResult.status)    ? globalResult
				: null
			if (result) {
				setField(result.field ?? null)
				setValues({
					low: convertDbNumberToHtml(result.lowGrowth),
					med: convertDbNumberToHtml(result.medGrowth),
					hig: convertDbNumberToHtml(result.higGrowth),
				})
				setIndicators({
					low: result.lowGrowthIndicator || '',
					med: result.medGrowthIndicator || '',
					hig: result.higGrowthIndicator || '',
				})
			} else if (ctxResult?.status) {
				setFatalError(true)
			}
			setIsLoading(false)
		})
	}, [contextId])

	const handleValueChange = (key, raw) => {
		const numeric = raw.replace(/[^0-9,]|,(?=.*,)/g, '')
		setValues(v => ({ ...v, [key]: numeric }))
	}

	const validate = () => {
		const e = {
			low: !field || !values.low,
			med: !field || !values.med,
			hig: !field || !values.hig,
		}
		setErrors(e)
		forceUpdate()
		return !e.low && !e.med && !e.hig
	}

	const handleSave = () => {
		setIsSaving(true)
		if (validate()) {
			internalFetch(config.apiUrl + '/api/settings/growthParameters/', {
				method  : 'POST',
				headers : { 'content-type': 'application/json; charset=utf-8' },
				body    : JSON.stringify({
					contextId          : contextId ?? null,
					params: {
						field,
						lowGrowth          : values.low,
						medGrowth          : values.med,
						higGrowth          : values.hig,
						lowGrowthIndicator : indicators.low,
						medGrowthIndicator : indicators.med,
						higGrowthIndicator : indicators.hig,
					}
				})
			}).then(results => {
				setMessage(results.status
					? { severity: 'error',   text: 'Erro ao salvar parâmetros.' }
					: { severity: 'success', text: 'Parâmetros salvos com sucesso.' }
				)
				if (!results.status) onSaved?.()
				setIsSaving(false)
			})
		} else {
			setMessage({ severity: 'error', text: 'Preencha todos os campos.' })
			setIsSaving(false)
		}
	}

	if (fatalError) return <GeneralError message="Erro ao carregar parâmetros de crescimento" />

	return (
		<Paper variant="outlined" sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
			<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
				Parâmetros de Crescimento
			</Typography>

			{isLoading ? <Loading paddingBottom={2} /> : (
				<UserParametersContext.Provider value={{ fields }}>
					{message && (
						<Alert severity={message.severity} sx={{ mb: 1.5, py: 0 }} onClose={() => setMessage(null)}>
							{message.text}
						</Alert>
					)}

					<Box mb={2}>
						<FieldList size="small" variant="growth" value={field} onChange={f => setField(f)} error={errors.low || errors.med || errors.hig} />
					</Box>

					<Divider sx={{ mb: 1.5 }} />

					<Stack spacing={1.5}>
						{LEVELS.map(({ key, label }) => (
							<Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

								<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
									<Typography variant="body2" sx={{ width: 52 }}>{label}</Typography>
									<Typography variant="body2" color="text.secondary">&gt;</Typography>
								</Stack>

								<Box sx={{ flex: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
									<TextField
										size="small"
										label="% mín."
										value={values[key]}
										onChange={e => handleValueChange(key, e.target.value)}
										error={errors[key]}
										inputProps={{ maxLength: 6 }}
										InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
										sx={{ width: { xs: '100%', sm: 110 }, flexShrink: 0 }}
									/>
									<Typography variant="body2" color="text.secondary">+</Typography>
									<FormControl size="small" sx={{ width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}>
										<InputLabel shrink>Indicador</InputLabel>
										<Select
											label="Indicador"
											displayEmpty
											value={indicators[key]}
											onChange={e => setIndicators(i => ({ ...i, [key]: e.target.value }))}
										>
											{INDICATORS.map(opt => (
												<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>

							</Box>
						))}
					</Stack>

					<Box flex={1} />
					<Stack direction="row" justifyContent="flex-end" mt={2}>
						<Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
							Salvar
						</Button>
					</Stack>
				</UserParametersContext.Provider>
			)}
		</Paper>
	)
}

export default GrowthPanel
